import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Money from '../lib/Money';
import AccountTag from '../lib/AccountTag';
import XBadge from '../lib/XBadge';
import EmptyStateNoRecord from '../lib/EmptyStateNoRecord';
import Loading from '../common/Loading';
import { XRPSCAN_API_URL } from '../../config';
import { handleResponse } from '../common/Helpers';
import AdvancedTable from '../lib/AdvancedTable';

const TokenHolder = (props) => {
    const { token } = props;
    const [ trustlines, setTrustlines ] = useState([]);
    const [ frozenTrustlines, setFrozenTrustlines ] = useState([]);
    const [ loading, setLoading ] = useState(false);

    const columns = useMemo(
        () => [
            {
                Header: `List of top 10,000 token holders`,
                columns: [
                {
                    Header: "#",
                    accessor: "index",
                },
                {
                    Header: "Account",
                    accessor: "account",
                    Cell: ({ cell: { value }}) => <span className='text-monospace'><AccountTag link>{value}</AccountTag></span>
                },
                {
                    Header: "Trustline limit",
                    accessor: "limit_peer",
                    className: "text-right text-muted",
                },
                {
                    Header: "Rippling",
                    accessor: "no_ripple",
                    className: "text-center",
                    Cell: ({ cell: { value }}) => value ?
                        <XBadge variant="primary">&nbsp;NO&nbsp;</XBadge>
                        :
                        <XBadge variant="success">YES</XBadge>
                },
                {
                    Header: "Balance ↓",
                    accessor: "balance",
                    className: "text-right",
                },
                ],
            },
            ],
            [],
    )

    /*
    * Add custom properties to represent Money etc.
    */
    const addProperties = useCallback((_trustlines) => {
        const freezeTag = <XBadge variant="danger" className="text-uppercase mr-1">FROZEN</XBadge>
        const _frozenTrustlines = {}
        const populatedTrustlines = _trustlines.map((tl, index) => {
            tl.index = index + 1
            tl.balance = <Money value={tl.balance * -1} currency={token.code} issuer={token.issuer} min={6} max={6}/>
            tl.limit_peer = <Money value={tl.limit_peer} currency={token.code} issuer={token.issuer} min={0} max={0}/>
            tl.limit_peer = tl.freeze_peer ? <>{freezeTag}{tl.limit_peer}</> : tl.limit_peer
            if (tl.freeze || tl.freeze_peer) {
                _frozenTrustlines[`${tl.account}.${tl.currency}`] = true
            }
            return tl;
        });
        setFrozenTrustlines(_frozenTrustlines);
        return populatedTrustlines;
    }, [token.code, token.issuer]);

    useEffect(() => {
        if (typeof token?.id === 'string') {
            setLoading(true);
            fetch(`${XRPSCAN_API_URL}/token/${token.id}/holders`)
            .then(handleResponse)
            .then((data) => {
                setTrustlines(addProperties(data));
            })
            .catch((error) => {
                setTrustlines([]);
            })
            .finally(() => {
                setLoading(false);
            });
            return () => {
                setTrustlines([]);
            }
        }
    }, [token.id, addProperties]);

    if (loading) {
        return <Loading/>;
    }

    if (!loading && trustlines && trustlines.length === 0) {
        return (
            <EmptyStateNoRecord title="No holders">
                This token is not held by any account on the XRPL.
            </EmptyStateNoRecord>
        )
    } else {
        return <AdvancedTable
            columns={columns}
            data={trustlines}
            getRowProps={row => ({
                className: (row.values && frozenTrustlines[`${row.values.account}.${row.values.currency}`]) ? 'table-active' : '',
            })}
            filterProperty='account'
            filterPlaceholder='Search token holder account'
            initialPageSize={100}
        />
    }
}

TokenHolder.propTypes = {
    token: PropTypes.string.isRequired,
}

export default TokenHolder;